import { Pipe, PipeTransform } from "@angular/core";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { Point } from "@utils/point";
import { Exclude, Expose, Transform } from "class-transformer";
import { IsBoolean, IsNumber, IsOptional, IsPositive, IsString } from "class-validator";

export class FloorplanState {
  private static readonly SCALE_FACTOR = 1.2;
  @Exclude()
  private _floorplan?: Floorplan;

  @IsNumber()
  @Expose({ name: "x" })
  @Transform((params) => params.value || 0, { toClassOnly: true })
  private _x: number = 0;

  @IsNumber()
  @Expose({ name: "y" })
  @Transform((params) => params.value || 0, { toClassOnly: true })
  private _y: number = 0;

  @IsNumber()
  @Expose({ name: "borderWidth" })
  @Transform((params) => params.value || 0, { toClassOnly: true })
  private _borderWidth: number = 0;

  @IsNumber()
  @Expose({ name: "borderHeight" })
  @Transform((params) => params.value || 0, { toClassOnly: true })
  private _borderHeight: number = 0;

  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "scale" })
  private _scale: number = 1;

  @IsNumber()
  @IsPositive({ message: "The value must be >=0" })
  @Transform((params) => params.value || 100, { toClassOnly: true })
  @Expose({ name: "opacity" })
  private _opacity: number = 100;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "exZones" })
  private _exZonesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "dustExZones" })
  private _dustExZonesVisible: boolean = true;

  @IsBoolean()
  @Expose({ name: "wildcardZones" })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  private _wildcardZonesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "monitoringAreas" })
  private _monitoringAreasVisible: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "coverageAreas" })
  private _coverageAreasVisible: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "dangerAreas" })
  private _dangerAreasVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "images" })
  private _imagesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "gasWarningCenters" })
  private _gasWarningCentersVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "transmitters" })
  private _transmittersVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "flameDetectors" })
  private _flameDetectorsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "alarmDevices" })
  private _alarmDevicesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "signalElements" })
  private _signalElementsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "plasticSigns" })
  private _plasticSignsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "floorplanTexts" })
  private _floorplanTextsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "floorplanFreeTexts" })
  private _floorplanFreeTextsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "measurementLines" })
  private _measurementLinesVisible: boolean = true;

  @IsBoolean()
  @IsOptional()
  @Expose({ name: "referenceMeasure" })
  private _referenceMeasureVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "airPaths" })
  private _airPathsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "pipelines" })
  private _pipelinesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "cableRoutes" })
  private _cableRoutesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "protectiveTubes" })
  private _protectiveTubesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "wires" })
  private _wiresVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "exZonesItemTextVisible" })
  private _exZonesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "dustExZonesItemTextVisible" })
  private _dustExZonesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "wildcardZonesItemTextVisible" })
  private _wildcardZonesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "dangerAreasItemTextVisible" })
  private _dangerAreasItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "imagesItemTextVisible" })
  private _imagesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "gasWarningCentersItemTextVisible" })
  private _gasWarningCentersItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "transmittersItemTextVisible" })
  private _transmittersItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "flameDetectorsItemTextVisible" })
  private _flameDetectorsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "alarmDevicesItemTextVisible" })
  private _alarmDevicesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "signalElementsItemTextVisible" })
  private _signalElementsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "plasticSignsItemTextVisible" })
  private _plasticSignsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "floorplanTextsItemTextVisible" })
  private _floorplanTextsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "floorplanFreeTextsItemTextVisible" })
  private _floorplanFreeTextsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "measurementLinesItemTextVisible" })
  private _measurementLinesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @IsOptional()
  @Expose({ name: "referenceMeasureItemTextVisible" })
  private _referenceMeasureItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "airPathsItemTextVisible" })
  private _airPathsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "pipelinesItemTextVisible" })
  private _pipelinesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "cableRoutesItemTextVisible" })
  private _cableRoutesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "protectiveTubesItemTextVisible" })
  private _protectiveTubesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "wiresItemTextVisible" })
  private _wiresItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "exZonesLocked" })
  private _exZonesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "dustExZonesLocked" })
  private _dustExZonesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "wildcardZonesLocked" })
  private _wildcardZonesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "dangerAreasLocked" })
  private _dangerAreasLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "imagesLocked" })
  private _imagesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "gasWarningCentersLocked" })
  private _gasWarningCentersLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "transmittersLocked" })
  private _transmittersLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "flameDetectorsLocked" })
  private _flameDetectorsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "alarmDevicesLocked" })
  private _alarmDevicesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "signalElementsLocked" })
  private _signalElementsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "plasticSignsLocked" })
  private _plasticSignsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "floorplanTextsLocked" })
  private _floorplanTextsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "floorplanFreeTextsLocked" })
  private _floorplanFreeTextsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "measurementLinesLocked" })
  private _measurementLinesLocked: boolean = false;

  @IsBoolean()
  @IsOptional()
  @Expose({ name: "referenceMeasureLocked" })
  private _referenceMeasureLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "airPathsLocked" })
  private _airPathsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "pipelinesLocked" })
  private _pipelinesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "cableRoutesLocked" })
  private _cableRoutesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "protectiveTubesLocked" })
  private _protectiveTubesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "wiresLocked" })
  private _wiresLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "patientMonitors" })
  private _patientMonitorsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "centralStationsVisible" })
  private _centralStationsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "telemetriesVisible" })
  private _telemetriesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "kvmsVisible" })
  private _kvmsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "displaysVisible" })
  private _displaysVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "utilitiesVisible" })
  private _utilitiesVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "isolationRooms" })
  private _isolationRoomsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "itRooms" })
  private _itRoomsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "operatingTheatres" })
  private _operatingTheatresVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "procedureRooms" })
  private _procedureRoomsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "standardRooms" })
  private _standardRoomsVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "patientMonitorsItemTextVisible" })
  private _patientMonitorsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "centralStationsItemTextVisible" })
  private _centralStationsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "telemetriesItemTextVisible" })
  private _telemetriesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "kvmsItemTextVisible" })
  private _kvmsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "displaysItemTextVisible" })
  private _displaysItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "utilitiesItemTextVisible" })
  private _utilitiesItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "isolationRoomsItemTextVisible" })
  private _isolationRoomsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "itRoomsItemTextVisible" })
  private _itRoomsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "operatingTheatresItemTextVisible" })
  private _operatingTheatresItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "procedureRoomsItemTextVisible" })
  private _procedureRoomsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "standardRoomsItemTextVisible" })
  private _standardRoomsItemTextVisible: boolean = true;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "patientMonitorsLocked" })
  private _patientMonitorsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "centralStationsLocked" })
  private _centralStationsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "telemetriesLocked" })
  private _telemetriesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "kvmsLocked" })
  private _kvmsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "displaysLocked" })
  private _displaysLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @Expose({ name: "utilitiesLocked" })
  private _utilitiesLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "isolationRoomsLocked" })
  private _isolationRoomsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "itRoomsLocked" })
  private _itRoomsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "operatingTheatresLocked" })
  private _operatingTheatresLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "procedureRoomsLocked" })
  private _procedureRoomsLocked: boolean = false;

  @IsBoolean()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "standardRoomsLocked" })
  private _standardRoomsLocked: boolean = false;

  @IsString()
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  @Expose({ name: "displayMode" })
  @Transform((params) => params.value || FloorplanDisplayMode.SCHEMATIC)
  private _displayMode: FloorplanDisplayMode = FloorplanDisplayMode.SCHEMATIC;

  @IsString()
  @Transform(({ value }) => (Object.values(FloorplanSelectionMode).includes(value) ? value : FloorplanSelectionMode.SINGLE), {
    toClassOnly: true,
  })
  @Expose({ name: "selectionMode" })
  private _selectionMode: FloorplanSelectionMode = FloorplanSelectionMode.SINGLE;

  @Exclude()
  private _selectedGroupId: string | undefined = undefined;

  init(floorplan: Floorplan) {
    this._floorplan = floorplan;
  }

  setVisibilityForAll(visible: boolean) {
    this.exZonesVisible = visible;
    this.dustExZonesVisible = visible;
    this.wildcardZonesVisible = visible;
    this.dangerAreasVisible = visible;
    this.imagesVisible = visible;
    this.measurementLinesVisible = visible;
    this.referenceMeasureVisible = visible;
    this.airPathsVisible = visible;
    this.pipelinesVisible = visible;
    this.cableRoutesVisible = visible;
    this.protectiveTubesVisible = visible;
    this.wiresVisible = visible;
    this.floorplanTextsVisible = visible;
    this._floorplanFreeTextsVisible = visible;
    this.transmittersVisible = visible;
    this.flameDetectorsVisible = visible;
    this.alarmDevicesVisible = visible;
    this.gasWarningCentersVisible = visible;
    this.signalElementsVisible = visible;
    this.plasticSignsVisible = visible;
    this.patientMonitorsVisible = visible;
    this.centralStationsVisible = visible;
    this.telemetriesVisible = visible;
    this.kvmsVisible = visible;
    this.displaysVisible = visible;
    this.utilitiesVisible = visible;
    this.isolationRoomsVisible = visible;
    this.itRoomsVisible = visible;
    this.operatingTheatresVisible = visible;
    this.procedureRoomsVisible = visible;
    this.standardRoomsVisible = visible;
  }

  updateGlobalItemTextVisibility(visible: boolean) {
    this.updateExZonesItemTextVisibility(visible);
    this.updateDustExZonesItemTextVisibility(visible);
    this.updateWildcardZonesItemTextVisibility(visible);
    this.updateDangerAreasItemTextVisibility(visible);
    this.updateImagesItemTextVisibility(visible);
    this.updateMeasurementLinesItemTextVisibility(visible);
    this.updateAirPathsItemTextVisibility(visible);
    this.updatePipelinesItemTextVisibility(visible);
    this.updateCableRoutesItemTextVisibility(visible);
    this.updateProtectiveTubesItemTextVisibility(visible);
    this.updateWiresItemTextVisibility(visible);
    this.updateFloorplanTextsItemTextVisibility(visible);
    this.updateFloorplanFreeTextsItemTextVisibility(visible);
    this.updateTransmittersItemTextVisibility(visible);
    this.updateFlameDetectorsItemTextVisibility(visible);
    this.updateAlarmDevicesItemTextVisibility(visible);
    this.updateGasWarningCentersItemTextVisibility(visible);
    this.updateSignalElementsItemTextVisibility(visible);
    this.updatePlasticSignsItemTextVisibility(visible);
    this.updateReferenceMeasureItemTextVisibility(visible);
    this.updatePatientMonitorsItemTextVisibility(visible);
    this.updateCentralStationsItemTextVisibility(visible);
    this.updateTelemetriesItemTextVisibility(visible);
    this.updateKvmsItemTextVisibility(visible);
    this.updateDisplaysItemTextVisibility(visible);
    this.updateUtilitiesItemTextVisibility(visible);
    this.updateIsolationRoomsItemTextVisibility(visible);
    this.updateItRoomsItemTextVisibility(visible);
    this.updateOperatingTheatresItemTextVisibility(visible);
    this.updateProcedureRoomsItemTextVisibility(visible);
    this.updateStandardRoomsItemTextVisibility(visible);

    this.publishGlobalItemTextVisibilityChanged();
  }

  setLockForAll(locked: boolean) {
    this.exZonesLocked = locked;
    this.dustExZonesLocked = locked;
    this.wildcardZonesLocked = locked;
    this.dangerAreasLocked = locked;
    this.imagesLocked = locked;
    this.measurementLinesLocked = locked;
    this.referenceMeasureLocked = locked;
    this.airPathsLocked = locked;
    this.pipelinesLocked = locked;
    this.cableRoutesLocked = locked;
    this.protectiveTubesLocked = locked;
    this.wiresLocked = locked;
    this.floorplanTextsLocked = locked;
    this.transmittersLocked = locked;
    this.flameDetectorsLocked = locked;
    this.alarmDevicesLocked = locked;
    this.gasWarningCentersLocked = locked;
    this.signalElementsLocked = locked;
    this.plasticSignsLocked = locked;
    this.patientMonitorsLocked = locked;
    this.centralStationsLocked = locked;
    this.telemetriesLocked = locked;
    this.kvmsLocked = locked;
    this.displaysLocked = locked;
    this.utilitiesLocked = locked;
    this.isolationRoomsLocked = locked;
    this.itRoomsLocked = locked;
    this.operatingTheatresLocked = locked;
    this.procedureRoomsLocked = locked;
    this.standardRoomsLocked = locked;
  }

  zoomIn(referencePoint: Point) {
    this.scaleAndUpdatePosition(FloorplanState.SCALE_FACTOR, referencePoint);
  }

  zoomOut(referencePoint: Point) {
    this.scaleAndUpdatePosition(1 / FloorplanState.SCALE_FACTOR, referencePoint);
  }

  updatePosition(x: number, y: number) {
    this._x = x;
    this._y = y;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_POSITION_UPDATED, this);
  }

  updateBorderShape(borderShape: { width: number; height: number }) {
    this._borderWidth = borderShape.width;
    this._borderHeight = borderShape.height;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_BORDER_SHAPE_UPDATED, this);
  }

  updateScaleAndPosition(scale: number, x: number, y: number) {
    this._scale = scale;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_SCALE_UPDATED, this);
    this.updatePosition(x, y);
  }

  get floorplan(): Floorplan {
    if (!this._floorplan) {
      throw Error("Floorplan must be set before accessing the state");
    }
    return this._floorplan;
  }

  get scale(): number {
    return this._scale;
  }

  get x(): number {
    return this._x;
  }

  get y(): number {
    return this._y;
  }

  get borderWidth(): number {
    return this._borderWidth;
  }

  get borderHeight(): number {
    return this._borderHeight;
  }

  get selectedGroupId(): string | undefined {
    return this._selectedGroupId;
  }

  set selectedGroupId(value: string | undefined) {
    this._selectedGroupId = value;
  }

  get monitoringAreasVisible(): boolean {
    return this._monitoringAreasVisible;
  }

  set monitoringAreasVisible(visible: boolean) {
    if (this._monitoringAreasVisible === visible) return;
    this._monitoringAreasVisible = visible;
    this.publishVisibilityChanged();
  }

  get coverageAreasVisible(): boolean {
    return this._coverageAreasVisible;
  }

  set coverageAreasVisible(visible: boolean) {
    if (this._coverageAreasVisible === visible) return;
    this._coverageAreasVisible = visible;
    this.publishVisibilityChanged();
  }

  get exZonesVisible(): boolean {
    return this._exZonesVisible;
  }

  get dustExZonesVisible(): boolean {
    return this._dustExZonesVisible;
  }

  get wildcardZonesVisible(): boolean {
    return this._wildcardZonesVisible;
  }

  set exZonesVisible(visible: boolean) {
    if (this._exZonesVisible === visible) return;
    this._exZonesVisible = visible;
    this.publishVisibilityChanged();
  }

  set dustExZonesVisible(visible: boolean) {
    if (this._dustExZonesVisible === visible) return;
    this._dustExZonesVisible = visible;
    this.publishVisibilityChanged();
  }

  set wildcardZonesVisible(visible: boolean) {
    if (this._wildcardZonesVisible === visible) return;
    this._wildcardZonesVisible = visible;
    this.publishVisibilityChanged();
  }

  get exZonesItemTextVisible(): boolean {
    return this._exZonesItemTextVisible;
  }

  get dustExZonesItemTextVisible(): boolean {
    return this._dustExZonesItemTextVisible;
  }

  get wildcardZonesItemTextVisible(): boolean {
    return this._wildcardZonesItemTextVisible;
  }

  set exZonesItemTextVisible(visible: boolean) {
    if (this._exZonesItemTextVisible === visible) return;
    this._exZonesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.EX_ZONES_ITEM_TEXT_VISIBILITY_CHANGED, this.exZonesItemTextVisible);
  }

  set dustExZonesItemTextVisible(visible: boolean) {
    if (this._dustExZonesItemTextVisible === visible) return;
    this._dustExZonesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.DUST_EX_ZONES_ITEM_TEXT_VISIBILITY_CHANGED, this.dustExZonesItemTextVisible);
  }

  set wildcardZonesItemTextVisible(visible: boolean) {
    if (this._wildcardZonesItemTextVisible === visible) return;
    this._wildcardZonesItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.WILDCARD_ZONES_ITEM_TEXT_VISIBILITY_CHANGED,
      this.wildcardZonesItemTextVisible,
    );
  }

  get exZonesLocked(): boolean {
    return this._exZonesLocked;
  }

  get dustExZonesLocked(): boolean {
    return this._dustExZonesLocked;
  }

  get wildcardZonesLocked(): boolean {
    return this._wildcardZonesLocked;
  }

  set exZonesLocked(locked: boolean) {
    if (this._exZonesLocked === locked) return;
    this._exZonesLocked = locked;
    this.updateLock(this.floorplan.exZones, locked);
  }

  set dustExZonesLocked(locked: boolean) {
    if (this._dustExZonesLocked === locked) return;
    this._dustExZonesLocked = locked;
    this.updateLock(this.floorplan.dustExZones, locked);
  }

  set wildcardZonesLocked(locked: boolean) {
    if (this._wildcardZonesLocked === locked) return;
    this._wildcardZonesLocked = locked;
    this.updateLock(this.floorplan.wildcardZones, locked);
  }

  refreshExZonesLock() {
    this.refreshLock(this.floorplan.exZones, this._exZonesLocked, (locked) => (this._exZonesLocked = locked));
  }

  refreshDustExZonesLock() {
    this.refreshLock(this.floorplan.dustExZones, this._dustExZonesLocked, (locked) => (this._dustExZonesLocked = locked));
  }

  refreshWildcardZonesLock() {
    this.refreshLock(this.floorplan.wildcardZones, this._wildcardZonesLocked, (locked) => (this._wildcardZonesLocked = locked));
  }

  get dangerAreasVisible(): boolean {
    return this._dangerAreasVisible;
  }

  set dangerAreasVisible(visible: boolean) {
    if (this._dangerAreasVisible === visible) return;
    this._dangerAreasVisible = visible;
    this.publishVisibilityChanged();
  }

  get dangerAreasItemTextVisible(): boolean {
    return this._dangerAreasItemTextVisible;
  }

  set dangerAreasItemTextVisible(visible: boolean) {
    if (this._dangerAreasItemTextVisible === visible) return;
    this._dangerAreasItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.DANGER_AREAS_ITEM_TEXT_VISIBILITY_CHANGED, this.dangerAreasItemTextVisible);
  }

  get dangerAreasLocked(): boolean {
    return this._dangerAreasLocked;
  }

  set dangerAreasLocked(locked: boolean) {
    if (this._dangerAreasLocked === locked) return;
    this._dangerAreasLocked = locked;
    this.updateLock(this.floorplan.dangerAreas, locked);
  }

  refreshDangerAreasLock() {
    this.refreshLock(this.floorplan.dangerAreas, this._dangerAreasLocked, (locked) => (this._dangerAreasLocked = locked));
  }

  get gasWarningCentersVisible(): boolean {
    return this._gasWarningCentersVisible;
  }

  set gasWarningCentersVisible(visible: boolean) {
    if (this._gasWarningCentersVisible === visible) return;
    this._gasWarningCentersVisible = visible;
    this.publishVisibilityChanged();
  }

  get gasWarningCentersItemTextVisible(): boolean {
    return this._gasWarningCentersItemTextVisible;
  }

  set gasWarningCentersItemTextVisible(visible: boolean) {
    if (this._gasWarningCentersItemTextVisible === visible) return;
    this._gasWarningCentersItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.GAS_WARNING_CENTERS_ITEM_TEXT_VISIBILITY_CHANGED,
      this.gasWarningCentersItemTextVisible,
    );
  }

  get gasWarningCentersLocked(): boolean {
    return this._gasWarningCentersLocked;
  }

  set gasWarningCentersLocked(locked: boolean) {
    if (this._gasWarningCentersLocked === locked) return;
    this._gasWarningCentersLocked = locked;
    this.updateLock([...this.floorplan.gasWarningCenters, ...this.floorplan.gasWarningCenterPlaceholders], locked);
  }

  refreshGasWarningCentersLock() {
    this.refreshLock(
      [...this.floorplan.gasWarningCenters, ...this.floorplan.gasWarningCenterPlaceholders],
      this._gasWarningCentersLocked,
      (locked) => (this._gasWarningCentersLocked = locked),
    );
  }

  get transmittersVisible(): boolean {
    return this._transmittersVisible;
  }

  set transmittersVisible(visible: boolean) {
    if (this._transmittersVisible === visible) return;
    this._transmittersVisible = visible;
    this.publishVisibilityChanged();
  }

  get transmittersItemTextVisible(): boolean {
    return this._transmittersItemTextVisible;
  }

  set transmittersItemTextVisible(visible: boolean) {
    if (this._transmittersItemTextVisible === visible) return;
    this._transmittersItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.TRANSMITTERS_ITEM_TEXT_VISIBILITY_CHANGED, this.transmittersItemTextVisible);
  }

  get transmittersLocked(): boolean {
    return this._transmittersLocked;
  }

  set transmittersLocked(locked: boolean) {
    if (this._transmittersLocked === locked) return;
    this._transmittersLocked = locked;
    this.updateLock([...this.floorplan.transmitters, ...this.floorplan.transmitterPlaceholders], locked);
  }

  refreshTransmittersLock() {
    this.refreshLock(
      [...this.floorplan.transmitters, ...this.floorplan.transmitterPlaceholders],
      this._transmittersLocked,
      (locked) => (this._transmittersLocked = locked),
    );
  }

  get flameDetectorsVisible(): boolean {
    return this._flameDetectorsVisible;
  }

  set flameDetectorsVisible(visible: boolean) {
    if (this._flameDetectorsVisible === visible) return;
    this._flameDetectorsVisible = visible;
    this.publishVisibilityChanged();
  }

  get flameDetectorsItemTextVisible(): boolean {
    return this._flameDetectorsItemTextVisible;
  }

  set flameDetectorsItemTextVisible(visible: boolean) {
    if (this._flameDetectorsItemTextVisible === visible) return;
    this._flameDetectorsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.FLAME_DETECTORS_ITEM_TEXT_VISIBILITY_CHANGED,
      this.flameDetectorsItemTextVisible,
    );
  }

  get flameDetectorsLocked(): boolean {
    return this._flameDetectorsLocked;
  }

  set flameDetectorsLocked(locked: boolean) {
    if (this._flameDetectorsLocked === locked) return;
    this._flameDetectorsLocked = locked;
    this.updateLock([...this.floorplan.flameDetectors, ...this.floorplan.flameDetectorPlaceholders], locked);
  }

  refreshFlameDetectorsLock() {
    this.refreshLock(
      [...this.floorplan.flameDetectors, ...this.floorplan.flameDetectorPlaceholders],
      this._flameDetectorsLocked,
      (locked) => (this._flameDetectorsLocked = locked),
    );
  }

  get alarmDevicesVisible(): boolean {
    return this._alarmDevicesVisible;
  }

  set alarmDevicesVisible(visible: boolean) {
    if (this._alarmDevicesVisible === visible) return;
    this._alarmDevicesVisible = visible;
    this.publishVisibilityChanged();
  }

  get alarmDevicesItemTextVisible(): boolean {
    return this._alarmDevicesItemTextVisible;
  }

  set alarmDevicesItemTextVisible(visible: boolean) {
    if (this._alarmDevicesItemTextVisible === visible) return;
    this._alarmDevicesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.ALARM_DEVICES_ITEM_TEXT_VISIBILITY_CHANGED, this.alarmDevicesItemTextVisible);
  }

  get alarmDevicesLocked(): boolean {
    return this._alarmDevicesLocked;
  }

  set alarmDevicesLocked(locked: boolean) {
    if (this._alarmDevicesLocked === locked) return;
    this._alarmDevicesLocked = locked;
    this.updateLock([...this.floorplan.alarmDevices, ...this.floorplan.alarmDevicePlaceholders], locked);
  }

  refreshAlarmDevicesLock() {
    this.refreshLock(
      [...this.floorplan.alarmDevices, ...this.floorplan.alarmDevicePlaceholders],
      this._alarmDevicesLocked,
      (locked) => (this._alarmDevicesLocked = locked),
    );
  }

  get signalElementsVisible(): boolean {
    return this._signalElementsVisible;
  }

  set signalElementsVisible(visible: boolean) {
    if (this._signalElementsVisible === visible) return;
    this._signalElementsVisible = visible;
    this.publishVisibilityChanged();
  }

  get signalElementsItemTextVisible(): boolean {
    return this._signalElementsItemTextVisible;
  }

  set signalElementsItemTextVisible(visible: boolean) {
    if (this._signalElementsItemTextVisible === visible) return;
    this._signalElementsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.SIGNAL_ELEMENTS_ITEM_TEXT_VISIBILITY_CHANGED,
      this.signalElementsItemTextVisible,
    );
  }

  get signalElementsLocked(): boolean {
    return this._signalElementsLocked;
  }

  set signalElementsLocked(locked: boolean) {
    if (this._signalElementsLocked === locked) return;
    this._signalElementsLocked = locked;
    this.updateLock([...this.floorplan.signalElements, ...this.floorplan.signalElementPlaceholders], locked);
  }

  refreshSignalElementsLock() {
    this.refreshLock(
      [...this.floorplan.signalElements, ...this.floorplan.signalElementPlaceholders],
      this._signalElementsLocked,
      (locked) => (this._signalElementsLocked = locked),
    );
  }

  get plasticSignsVisible(): boolean {
    return this._plasticSignsVisible;
  }

  set plasticSignsVisible(visible: boolean) {
    if (this._plasticSignsVisible === visible) return;
    this._plasticSignsVisible = visible;
    this.publishVisibilityChanged();
  }

  get plasticSignsItemTextVisible(): boolean {
    return this._plasticSignsItemTextVisible;
  }

  set plasticSignsItemTextVisible(visible: boolean) {
    if (this._plasticSignsItemTextVisible === visible) return;
    this._plasticSignsItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.PLASTIC_SIGNS_ITEM_TEXT_VISIBILITY_CHANGED, this.plasticSignsItemTextVisible);
  }

  get plasticSignsLocked(): boolean {
    return this._plasticSignsLocked;
  }

  set plasticSignsLocked(locked: boolean) {
    if (this._plasticSignsLocked === locked) return;
    this._plasticSignsLocked = locked;
    this.updateLock([...this.floorplan.plasticSigns, ...this.floorplan.plasticSignPlaceholders], locked);
  }

  refreshPlasticSignsLock() {
    this.refreshLock(
      [...this.floorplan.plasticSigns, ...this.floorplan.plasticSignPlaceholders],
      this._plasticSignsLocked,
      (locked) => (this._plasticSignsLocked = locked),
    );
  }

  get imagesVisible(): boolean {
    return this._imagesVisible;
  }

  set imagesVisible(visible: boolean) {
    if (this._imagesVisible === visible) return;
    this._imagesVisible = visible;
    this.publishVisibilityChanged();
  }

  get imagesItemTextVisible(): boolean {
    return this._imagesItemTextVisible;
  }

  set imagesItemTextVisible(visible: boolean) {
    if (this._imagesItemTextVisible === visible) return;
    this._imagesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.IMAGES_ITEM_TEXT_VISIBILITY_CHANGED, this.imagesItemTextVisible);
  }

  get imagesLocked(): boolean {
    return this._imagesLocked;
  }

  set imagesLocked(locked: boolean) {
    if (this._imagesLocked === locked) return;
    this._imagesLocked = locked;
    this.updateLock(this.floorplan.images, locked);
  }

  refreshImagesLock() {
    this.refreshLock(this.floorplan.images, this._imagesLocked, (locked) => (this._imagesLocked = locked));
  }

  get floorplanTextsVisible(): boolean {
    return this._floorplanTextsVisible;
  }

  set floorplanTextsVisible(visible: boolean) {
    if (this._floorplanTextsVisible === visible) return;
    this._floorplanTextsVisible = visible;
    this.publishVisibilityChanged();
  }

  get floorplanFreeTextsVisible(): boolean {
    return this._floorplanFreeTextsVisible;
  }

  set floorplanFreeTextsVisible(visible: boolean) {
    if (this._floorplanFreeTextsVisible === visible) return;
    this._floorplanFreeTextsVisible = visible;
    this.publishVisibilityChanged();
  }

  get floorplanTextsItemTextVisible(): boolean {
    return this._floorplanTextsItemTextVisible;
  }

  set floorplanTextsItemTextVisible(visible: boolean) {
    if (this._floorplanTextsItemTextVisible === visible) return;
    this._floorplanTextsItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.TEXTS_ITEM_TEXT_VISIBILITY_CHANGED, this.floorplanTextsItemTextVisible);
  }

  get floorplanFreeTextsItemTextVisible(): boolean {
    return this._floorplanFreeTextsItemTextVisible;
  }

  set floorplanFreeTextsItemTextVisible(visible: boolean) {
    if (this._floorplanFreeTextsItemTextVisible === visible) return;
    this._floorplanFreeTextsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.FREE_TEXTS_ITEM_TEXT_VISIBILITY_CHANGED,
      this.floorplanFreeTextsItemTextVisible,
    );
  }

  get floorplanTextsLocked(): boolean {
    return this._floorplanTextsLocked;
  }

  set floorplanTextsLocked(locked: boolean) {
    if (this._floorplanTextsLocked === locked) return;
    this._floorplanTextsLocked = locked;
    this.updateLock(this.floorplan.floorplanTexts, locked);
  }

  refreshFloorplanTextsLock() {
    this.refreshLock(
      this.floorplan.floorplanTexts,
      this._floorplanTextsLocked,
      (locked) => (this._floorplanTextsLocked = locked),
    );
  }

  get floorplanFreeTextsLocked(): boolean {
    return this._floorplanFreeTextsLocked;
  }

  set floorplanFreeTextsLocked(locked: boolean) {
    if (this._floorplanFreeTextsLocked === locked) return;
    this._floorplanFreeTextsLocked = locked;
    this.updateLock(this.floorplan.floorplanFreeTexts, locked);
  }

  refreshFloorplanFreeTextsLock() {
    this.refreshLock(
      this.floorplan.floorplanFreeTexts,
      this._floorplanFreeTextsLocked,
      (locked) => (this._floorplanFreeTextsLocked = locked),
    );
  }

  get measurementLinesVisible(): boolean {
    return this._measurementLinesVisible;
  }

  set measurementLinesVisible(visible: boolean) {
    if (this._measurementLinesVisible === visible) return;
    this._measurementLinesVisible = visible;
    this.publishVisibilityChanged();
  }

  get measurementLinesItemTextVisible(): boolean {
    return this._measurementLinesItemTextVisible;
  }

  set measurementLinesItemTextVisible(visible: boolean) {
    if (this._measurementLinesItemTextVisible === visible) return;
    this._measurementLinesItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.MEASUREMENT_LINES_ITEM_TEXT_VISIBILITY_CHANGED,
      this.measurementLinesItemTextVisible,
    );
  }

  get measurementLinesLocked(): boolean {
    return this._measurementLinesLocked;
  }

  set measurementLinesLocked(locked: boolean) {
    if (this._measurementLinesLocked === locked) return;
    this._measurementLinesLocked = locked;
    this.updateLock(this.floorplan.measurementLines, locked);
  }

  refreshMeasurementLinesLock() {
    this.refreshLock(
      this.floorplan.measurementLines,
      this._measurementLinesLocked,
      (locked) => (this._measurementLinesLocked = locked),
    );
  }

  get referenceMeasureVisible(): boolean {
    return this._referenceMeasureVisible;
  }

  set referenceMeasureVisible(visible: boolean) {
    if (this._referenceMeasureVisible === visible) return;
    this._referenceMeasureVisible = visible;
    this.publishVisibilityChanged();
  }

  get referenceMeasureItemTextVisible(): boolean {
    return this._referenceMeasureItemTextVisible;
  }

  set referenceMeasureItemTextVisible(visible: boolean) {
    if (this._referenceMeasureItemTextVisible === visible) return;
    this._referenceMeasureItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.REFERENCE_MEASURE_ITEM_TEXT_VISIBILITY_CHANGED,
      this.referenceMeasureItemTextVisible,
    );
  }

  get referenceMeasureLocked(): boolean {
    return this._referenceMeasureLocked;
  }

  set referenceMeasureLocked(locked: boolean) {
    if (!this.floorplan.referenceMeasure || this._referenceMeasureLocked === locked) return;
    this._referenceMeasureLocked = locked;
    this.updateLock([this.floorplan.referenceMeasure!], locked);
  }

  refreshReferenceMeasureLock() {
    if (this.floorplan.referenceMeasure) {
      this.refreshLock(
        [this.floorplan.referenceMeasure],
        this._referenceMeasureLocked,
        (locked) => (this._referenceMeasureLocked = locked),
      );
    }
  }

  get airPathsVisible(): boolean {
    return this._airPathsVisible;
  }

  set airPathsVisible(visible: boolean) {
    if (this._airPathsVisible === visible) return;
    this._airPathsVisible = visible;
    this.publishVisibilityChanged();
  }

  get airPathsItemTextVisible(): boolean {
    return this._airPathsItemTextVisible;
  }

  set airPathsItemTextVisible(visible: boolean) {
    if (this._airPathsItemTextVisible === visible) return;
    this._airPathsItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.AIR_PATHS_ITEM_TEXT_VISIBILITY_CHANGED, this.airPathsItemTextVisible);
  }

  get airPathsLocked(): boolean {
    return this._airPathsLocked;
  }

  set airPathsLocked(locked: boolean) {
    if (this._airPathsLocked === locked) return;
    this._airPathsLocked = locked;
    this.updateLock(this.floorplan.airPaths, locked);
  }

  refreshAirPathsLock() {
    this.refreshLock(this.floorplan.airPaths, this._airPathsLocked, (locked) => (this._airPathsLocked = locked));
  }

  get pipelinesVisible(): boolean {
    return this._pipelinesVisible;
  }

  set pipelinesVisible(visible: boolean) {
    if (this._pipelinesVisible === visible) return;
    this._pipelinesVisible = visible;
    this.publishVisibilityChanged();
  }

  get pipelinesItemTextVisible(): boolean {
    return this._pipelinesItemTextVisible;
  }

  set pipelinesItemTextVisible(visible: boolean) {
    if (this._pipelinesItemTextVisible === visible) return;
    this._pipelinesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.PIPELINES_ITEM_TEXT_VISIBILITY_CHANGED, this.pipelinesItemTextVisible);
  }

  get pipelinesLocked(): boolean {
    return this._pipelinesLocked;
  }

  set pipelinesLocked(locked: boolean) {
    if (this._pipelinesLocked === locked) return;
    this._pipelinesLocked = locked;
    this.updateLock(this.floorplan.pipelines, locked);
  }

  refreshPipelinesLock() {
    this.refreshLock(this.floorplan.pipelines, this._pipelinesLocked, (locked) => (this._pipelinesLocked = locked));
  }

  get cableRoutesVisible(): boolean {
    return this._cableRoutesVisible;
  }

  set cableRoutesVisible(visible: boolean) {
    if (this._cableRoutesVisible === visible) return;
    this._cableRoutesVisible = visible;
    this.publishVisibilityChanged();
  }

  get cableRoutesItemTextVisible(): boolean {
    return this._cableRoutesItemTextVisible;
  }

  set cableRoutesItemTextVisible(visible: boolean) {
    if (this._cableRoutesItemTextVisible === visible) return;
    this._cableRoutesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.CABLE_ROUTES_ITEM_TEXT_VISIBILITY_CHANGED, this.cableRoutesItemTextVisible);
  }

  get cableRoutesLocked(): boolean {
    return this._cableRoutesLocked;
  }

  set cableRoutesLocked(locked: boolean) {
    if (this._cableRoutesLocked === locked) return;
    this._cableRoutesLocked = locked;
    this.updateLock(this.floorplan.cableRoutes, locked);
  }

  refreshCableRoutesLock() {
    this.refreshLock(this.floorplan.cableRoutes, this._cableRoutesLocked, (locked) => (this._cableRoutesLocked = locked));
  }

  get protectiveTubesVisible(): boolean {
    return this._protectiveTubesVisible;
  }

  set protectiveTubesVisible(visible: boolean) {
    if (this._protectiveTubesVisible === visible) return;
    this._protectiveTubesVisible = visible;
    this.publishVisibilityChanged();
  }

  get protectiveTubesItemTextVisible(): boolean {
    return this._protectiveTubesItemTextVisible;
  }

  set protectiveTubesItemTextVisible(visible: boolean) {
    if (this._protectiveTubesItemTextVisible === visible) return;
    this._protectiveTubesItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.PROTECTIVE_TUBES_ITEM_TEXT_VISIBILITY_CHANGED,
      this.protectiveTubesItemTextVisible,
    );
  }

  get protectiveTubesLocked(): boolean {
    return this._protectiveTubesLocked;
  }

  set protectiveTubesLocked(locked: boolean) {
    if (this._protectiveTubesLocked === locked) return;
    this._protectiveTubesLocked = locked;
    this.updateLock(this.floorplan.protectiveTubes, locked);
  }

  refreshProtectiveTubesLock() {
    this.refreshLock(
      this.floorplan.protectiveTubes,
      this._protectiveTubesLocked,
      (locked) => (this._protectiveTubesLocked = locked),
    );
  }

  get wiresVisible(): boolean {
    return this._wiresVisible;
  }

  set wiresVisible(visible: boolean) {
    if (this._wiresVisible === visible) return;
    this._wiresVisible = visible;
    this.publishVisibilityChanged();
  }

  get wiresLocked(): boolean {
    return this._wiresLocked;
  }

  get wiresItemTextVisible(): boolean {
    return this._wiresItemTextVisible;
  }

  set wiresItemTextVisible(visible: boolean) {
    if (this._wiresItemTextVisible === visible) return;
    this._wiresItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.WIRES_ITEM_TEXT_VISIBILITY_CHANGED, this.wiresItemTextVisible);
  }

  set wiresLocked(locked: boolean) {
    if (this._wiresLocked === locked) return;
    this._wiresLocked = locked;
    this.updateLock(this.floorplan.wires, locked);
  }

  get patientMonitorsVisible(): boolean {
    return this._patientMonitorsVisible;
  }

  set patientMonitorsVisible(visible: boolean) {
    if (this._patientMonitorsVisible === visible) return;
    this._patientMonitorsVisible = visible;
    this.publishVisibilityChanged();
  }

  get patientMonitorsItemTextVisible(): boolean {
    return this._patientMonitorsItemTextVisible;
  }

  set patientMonitorsItemTextVisible(visible: boolean) {
    if (this._patientMonitorsItemTextVisible === visible) return;
    this._patientMonitorsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.PATIENT_MONITORS_ITEM_TEXT_VISIBILITY_CHANGED,
      this._patientMonitorsItemTextVisible,
    );
  }

  get patientMonitorsLocked(): boolean {
    return this._patientMonitorsLocked;
  }

  set patientMonitorsLocked(locked: boolean) {
    if (this._patientMonitorsLocked === locked) return;
    this._patientMonitorsLocked = locked;
    this.updateLock([...this.floorplan.patientMonitors, ...this.floorplan.patientMonitorPlaceholders], locked);
  }

  refreshPatientMonitorsLock() {
    this.refreshLock(
      this.floorplan.patientMonitors,
      this._patientMonitorsLocked,
      (locked) => (this._patientMonitorsLocked = locked),
    );
  }

  get centralStationsVisible(): boolean {
    return this._centralStationsVisible;
  }

  set centralStationsVisible(visible: boolean) {
    if (this._centralStationsVisible === visible) return;
    this._centralStationsVisible = visible;
    this.publishVisibilityChanged();
  }

  get centralStationsItemTextVisible(): boolean {
    return this._centralStationsItemTextVisible;
  }

  set centralStationsItemTextVisible(visible: boolean) {
    if (this._centralStationsItemTextVisible === visible) return;
    this._centralStationsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.CENTRAL_STATIONS_ITEM_TEXT_VISIBILITY_CHANGED,
      this._centralStationsItemTextVisible,
    );
  }

  get centralStationsLocked(): boolean {
    return this._centralStationsLocked;
  }

  set centralStationsLocked(locked: boolean) {
    if (this._centralStationsLocked === locked) return;
    this._centralStationsLocked = locked;
    this.updateLock([...this.floorplan.centralStations, ...this.floorplan.centralStationPlaceholders], locked);
  }

  refreshCentralStationsLock() {
    this.refreshLock(
      this.floorplan.centralStations,
      this._centralStationsLocked,
      (locked) => (this._centralStationsLocked = locked),
    );
  }

  get telemetriesVisible(): boolean {
    return this._telemetriesVisible;
  }

  set telemetriesVisible(visible: boolean) {
    if (this._telemetriesVisible === visible) return;
    this._telemetriesVisible = visible;
    this.publishVisibilityChanged();
  }

  get telemetriesItemTextVisible(): boolean {
    return this._telemetriesItemTextVisible;
  }

  set telemetriesItemTextVisible(visible: boolean) {
    if (this._telemetriesItemTextVisible === visible) return;
    this._telemetriesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.TELEMETRIES_ITEM_TEXT_VISIBILITY_CHANGED, this._telemetriesItemTextVisible);
  }

  get telemetriesLocked(): boolean {
    return this._telemetriesLocked;
  }

  set telemetriesLocked(locked: boolean) {
    if (this._telemetriesLocked === locked) return;
    this._telemetriesLocked = locked;
    this.updateLock([...this.floorplan.telemetries, ...this.floorplan.telemetryPlaceholders], locked);
  }

  get kvmsVisible(): boolean {
    return this._kvmsVisible;
  }

  set kvmsVisible(visible: boolean) {
    if (this._kvmsVisible === visible) return;
    this._kvmsVisible = visible;
    this.publishVisibilityChanged();
  }

  get kvmsItemTextVisible(): boolean {
    return this._kvmsItemTextVisible;
  }

  set kvmsItemTextVisible(visible: boolean) {
    if (this._kvmsItemTextVisible === visible) return;
    this._kvmsItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.KVMS_ITEM_TEXT_VISIBILITY_CHANGED, this._kvmsItemTextVisible);
  }

  get kvmsLocked(): boolean {
    return this._kvmsLocked;
  }

  set kvmsLocked(locked: boolean) {
    if (this._kvmsLocked === locked) return;
    this._kvmsLocked = locked;
    this.updateLock([...this.floorplan.kvms, ...this.floorplan.kvmPlaceholders], locked);
  }

  refreshTelemetriesLock() {
    this.refreshLock(this.floorplan.telemetries, this._telemetriesLocked, (locked) => (this._telemetriesLocked = locked));
  }

  refreshKvmsLock() {
    this.refreshLock(this.floorplan.kvms, this._kvmsLocked, (locked) => (this._kvmsLocked = locked));
  }

  get displaysVisible(): boolean {
    return this._displaysVisible;
  }

  set displaysVisible(visible: boolean) {
    if (this._displaysVisible === visible) return;
    this._displaysVisible = visible;
    this.publishVisibilityChanged();
  }

  get displaysItemTextVisible(): boolean {
    return this._displaysItemTextVisible;
  }

  set displaysItemTextVisible(visible: boolean) {
    if (this._displaysItemTextVisible === visible) return;
    this._displaysItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.DISPLAYS_ITEM_TEXT_VISIBILITY_CHANGED, this._displaysItemTextVisible);
  }

  get displaysLocked(): boolean {
    return this._displaysLocked;
  }

  set displaysLocked(locked: boolean) {
    if (this._displaysLocked === locked) return;
    this._displaysLocked = locked;
    this.updateLock([...this.floorplan.displays, ...this.floorplan.displayPlaceholders], locked);
  }

  refreshDisplaysLock() {
    this.refreshLock(this.floorplan.displays, this._displaysLocked, (locked) => (this._displaysLocked = locked));
  }

  get utilitiesVisible(): boolean {
    return this._utilitiesVisible;
  }

  set utilitiesVisible(visible: boolean) {
    if (this._utilitiesVisible === visible) return;
    this._utilitiesVisible = visible;
    this.publishVisibilityChanged();
  }

  get utilitiesItemTextVisible(): boolean {
    return this._utilitiesItemTextVisible;
  }

  set utilitiesItemTextVisible(visible: boolean) {
    if (this._utilitiesItemTextVisible === visible) return;
    this._utilitiesItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.UTILITIES_ITEM_TEXT_VISIBILITY_CHANGED, this._utilitiesItemTextVisible);
  }

  get utilitiesLocked(): boolean {
    return this._utilitiesLocked;
  }

  set utilitiesLocked(locked: boolean) {
    if (this._utilitiesLocked === locked) return;
    this._utilitiesLocked = locked;
    this.updateLock([...this.floorplan.utilities, ...this.floorplan.utilityPlaceholders], locked);
  }

  refreshUtilitiesLock() {
    this.refreshLock(this.floorplan.utilities, this._utilitiesLocked, (locked) => (this._utilitiesLocked = locked));
  }

  get isolationRoomsVisible(): boolean {
    return this._isolationRoomsVisible;
  }

  set isolationRoomsVisible(visible: boolean) {
    if (this._isolationRoomsVisible === visible) return;
    this._isolationRoomsVisible = visible;
    this.publishVisibilityChanged();
  }

  get isolationRoomsItemTextVisible(): boolean {
    return this._isolationRoomsItemTextVisible;
  }

  set isolationRoomsItemTextVisible(visible: boolean) {
    if (this._isolationRoomsItemTextVisible === visible) return;
    this._isolationRoomsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.ISOLATION_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED,
      this._isolationRoomsItemTextVisible,
    );
  }

  get isolationRoomsLocked(): boolean {
    return this._isolationRoomsLocked;
  }

  set isolationRoomsLocked(locked: boolean) {
    if (this._isolationRoomsLocked === locked) return;
    this._isolationRoomsLocked = locked;
    this.updateLock(this.floorplan.isolationRooms, locked);
  }

  refreshIsolationRoomsLock() {
    this.refreshLock(
      this.floorplan.isolationRooms,
      this._isolationRoomsLocked,
      (locked) => (this._isolationRoomsLocked = locked),
    );
  }

  get itRoomsVisible(): boolean {
    return this._itRoomsVisible;
  }

  set itRoomsVisible(visible: boolean) {
    if (this._itRoomsVisible === visible) return;
    this._itRoomsVisible = visible;
    this.publishVisibilityChanged();
  }

  get itRoomsItemTextVisible(): boolean {
    return this._itRoomsItemTextVisible;
  }

  set itRoomsItemTextVisible(visible: boolean) {
    if (this._itRoomsItemTextVisible === visible) return;
    this._itRoomsItemTextVisible = visible;
    this.floorplan.publishUpdate(FloorplanEventType.IT_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED, this._itRoomsItemTextVisible);
  }

  get itRoomsLocked(): boolean {
    return this._itRoomsLocked;
  }

  set itRoomsLocked(locked: boolean) {
    if (this._itRoomsLocked === locked) return;
    this._itRoomsLocked = locked;
    this.updateLock(this.floorplan.itRooms, locked);
  }

  refreshItRoomsLock() {
    this.refreshLock(this.floorplan.itRooms, this._itRoomsLocked, (locked) => (this._itRoomsLocked = locked));
  }

  get operatingTheatresVisible(): boolean {
    return this._operatingTheatresVisible;
  }

  set operatingTheatresVisible(visible: boolean) {
    if (this._operatingTheatresVisible === visible) return;
    this._operatingTheatresVisible = visible;
    this.publishVisibilityChanged();
  }

  get operatingTheatresItemTextVisible(): boolean {
    return this._operatingTheatresItemTextVisible;
  }

  set operatingTheatresItemTextVisible(visible: boolean) {
    if (this._operatingTheatresItemTextVisible === visible) return;
    this._operatingTheatresItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.OPERATING_THEATRES_ITEM_TEXT_VISIBILITY_CHANGED,
      this._operatingTheatresItemTextVisible,
    );
  }

  get operatingTheatresLocked(): boolean {
    return this._operatingTheatresLocked;
  }

  set operatingTheatresLocked(locked: boolean) {
    if (this._operatingTheatresLocked === locked) return;
    this._operatingTheatresLocked = locked;
    this.updateLock(this.floorplan.operatingTheatres, locked);
  }

  refreshOperatingTheatresLock() {
    this.refreshLock(
      this.floorplan.operatingTheatres,
      this._operatingTheatresLocked,
      (locked) => (this._operatingTheatresLocked = locked),
    );
  }

  get procedureRoomsVisible(): boolean {
    return this._procedureRoomsVisible;
  }

  set procedureRoomsVisible(visible: boolean) {
    if (this._procedureRoomsVisible === visible) return;
    this._procedureRoomsVisible = visible;
    this.publishVisibilityChanged();
  }

  get procedureRoomsItemTextVisible(): boolean {
    return this._procedureRoomsItemTextVisible;
  }

  set procedureRoomsItemTextVisible(visible: boolean) {
    if (this._procedureRoomsItemTextVisible === visible) return;
    this._procedureRoomsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.PROCEDURE_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED,
      this._procedureRoomsItemTextVisible,
    );
  }

  get procedureRoomsLocked(): boolean {
    return this._procedureRoomsLocked;
  }

  set procedureRoomsLocked(locked: boolean) {
    if (this._procedureRoomsLocked === locked) return;
    this._procedureRoomsLocked = locked;
    this.updateLock(this.floorplan.procedureRooms, locked);
  }

  refreshProcedureRoomsLock() {
    this.refreshLock(
      this.floorplan.procedureRooms,
      this._procedureRoomsLocked,
      (locked) => (this._procedureRoomsLocked = locked),
    );
  }

  get standardRoomsVisible(): boolean {
    return this._standardRoomsVisible;
  }

  set standardRoomsVisible(visible: boolean) {
    if (this._standardRoomsVisible === visible) return;
    this._standardRoomsVisible = visible;
    this.publishVisibilityChanged();
  }

  get standardRoomsItemTextVisible(): boolean {
    return this._standardRoomsItemTextVisible;
  }

  set standardRoomsItemTextVisible(visible: boolean) {
    if (this._standardRoomsItemTextVisible === visible) return;
    this._standardRoomsItemTextVisible = visible;
    this.floorplan.publishUpdate(
      FloorplanEventType.STANDARD_ROOMS_ITEM_TEXT_VISIBILITY_CHANGED,
      this._standardRoomsItemTextVisible,
    );
  }

  get standardRoomsLocked(): boolean {
    return this._standardRoomsLocked;
  }

  set standardRoomsLocked(locked: boolean) {
    if (this._standardRoomsLocked === locked) return;
    this._standardRoomsLocked = locked;
    this.updateLock(this.floorplan.standardRooms, locked);
  }

  refreshStandardRoomsLock() {
    this.refreshLock(this.floorplan.standardRooms, this._standardRoomsLocked, (locked) => (this._standardRoomsLocked = locked));
  }

  get opacity(): number {
    return this._opacity;
  }

  set opacity(value: number) {
    this._opacity = value;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_OPACITY_UPDATED, this);
  }

  get displayMode(): string {
    return this._displayMode;
  }

  set displayMode(value: FloorplanDisplayMode) {
    this._displayMode = value;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_DISPLAY_MODE_UPDATED, this);
  }

  get selectionMode(): FloorplanSelectionMode {
    return this._selectionMode;
  }

  set selectionMode(value: FloorplanSelectionMode) {
    this._selectionMode = value;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_SELECTION_MODE_UPDATED, this);
  }

  isGroupSelected(groupId: string) {
    return !!(this._selectedGroupId && this._selectedGroupId === groupId);
  }

  refreshWiresLock() {
    this.refreshLock(this.floorplan.wires, this._wiresLocked, (locked) => (this._wiresLocked = locked));
  }

  publishGlobalItemTextVisibilityChanged() {
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_ITEM_TEXT_VISIBILITY_CHANGED, this);
  }

  private refreshLock(items: FloorplanItem[], locked: boolean, lockFn: (locked: boolean) => void): void {
    const allItemsLocked = items.every((item) => item.locked);
    if (allItemsLocked && !locked) {
      lockFn(true);
      this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_LOCK_CHANGED, this);
    } else if (!allItemsLocked && locked) {
      lockFn(false);
      this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_LOCK_CHANGED, this);
    }
  }

  private updateLock(items: FloorplanItem[], locked: boolean): void {
    if (!this._floorplan) return;
    items.filter((item) => item.locked !== locked).forEach((item) => item.setLockedWithoutRefresh(locked));
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_LOCK_CHANGED, this);
  }
  private publishVisibilityChanged() {
    if (!this._floorplan) return;
    this.floorplan.publishUpdate(FloorplanEventType.FLOORPLAN_STATE_VISIBILITY_CHANGED, this);
  }

  private scaleAndUpdatePosition(factor: number, referencePoint: Point) {
    const newScale = Math.min(Math.max(factor * this._scale, 0.2), 5);
    if (newScale === this._scale) {
      return;
    }

    const mousePointTo = {
      x: referencePoint.x / this._scale - this._x! / this._scale,
      y: referencePoint.y / this._scale - this._y! / this._scale,
    };

    const newPos = new Point(
      (referencePoint.x / newScale - mousePointTo.x) * newScale,
      (referencePoint.y / newScale - mousePointTo.y) * newScale,
    );

    this.updateScaleAndPosition(newScale, newPos.x, newPos.y);
  }

  selectGroup(group: ProductItemGroup) {
    this._selectedGroupId = group.id;
    this.floorplan.publishUpdate(FloorplanEventType.GROUP_SELECTED, group);
  }

  deselectGroup(group: ProductItemGroup) {
    this._selectedGroupId = undefined;
    this.floorplan.publishUpdate(FloorplanEventType.GROUP_DESELECTED, group);
  }

  updateAlarmDevicesItemTextVisibility(visible: boolean) {
    [...this.floorplan.alarmDevices, ...this.floorplan.alarmDevicePlaceholders].forEach(
      (alarmDevice) => (alarmDevice.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.alarmDevicesItemTextVisible = visible;
  }

  updateGasWarningCentersItemTextVisibility(visible: boolean) {
    [...this.floorplan.gasWarningCenters, ...this.floorplan.gasWarningCenterPlaceholders].forEach(
      (gasWarningCenter) => (gasWarningCenter.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.gasWarningCentersItemTextVisible = visible;
  }

  updateTransmittersItemTextVisibility(visible: boolean) {
    [...this.floorplan.transmitters, ...this.floorplan.transmitterPlaceholders].forEach(
      (transmitter) => (transmitter.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.transmittersItemTextVisible = visible;
  }

  updateFlameDetectorsItemTextVisibility(visible: boolean) {
    [...this.floorplan.flameDetectors, ...this.floorplan.flameDetectorPlaceholders].forEach(
      (flameDetector) => (flameDetector.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.flameDetectorsItemTextVisible = visible;
  }

  updateSignalElementsItemTextVisibility(visible: boolean) {
    [...this.floorplan.signalElements, ...this.floorplan.signalElementPlaceholders].forEach(
      (signalElement) => (signalElement.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.signalElementsItemTextVisible = visible;
  }

  updatePlasticSignsItemTextVisibility(visible: boolean) {
    [...this.floorplan.plasticSigns, ...this.floorplan.plasticSignPlaceholders].forEach(
      (plasticSign) => (plasticSign.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.plasticSignsItemTextVisible = visible;
  }

  updateExZonesItemTextVisibility(visible: boolean) {
    this.floorplan.exZones.forEach((zone) => (zone.itemTextVisible = visible));
    this.floorplan.floorplanState.exZonesItemTextVisible = visible;
  }

  updateDustExZonesItemTextVisibility(visible: boolean) {
    this.floorplan.dustExZones.forEach((zone) => (zone.itemTextVisible = visible));
    this.floorplan.floorplanState.dustExZonesItemTextVisible = visible;
  }

  updateWildcardZonesItemTextVisibility(visible: boolean) {
    this.floorplan.wildcardZones.forEach((zone) => (zone.itemTextVisible = visible));
    this.floorplan.floorplanState.wildcardZonesItemTextVisible = visible;
  }

  updateDangerAreasItemTextVisibility(visible: boolean) {
    this.floorplan.dangerAreas.forEach((dangerArea) => (dangerArea.itemTextVisible = visible));
    this.floorplan.floorplanState.dangerAreasItemTextVisible = visible;
  }

  updateImagesItemTextVisibility(visible: boolean) {
    this.floorplan.images.forEach((image) => (image.itemTextVisible = visible));
    this.floorplan.floorplanState.imagesItemTextVisible = visible;
  }

  updateAirPathsItemTextVisibility(visible: boolean) {
    this.floorplan.airPaths.forEach((airPath) => (airPath.itemTextVisible = visible));
    this.floorplan.floorplanState.airPathsItemTextVisible = visible;
  }

  updateFloorplanTextsItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanTexts.forEach((text) => (text.itemTextVisible = visible));
    this.floorplan.floorplanState.floorplanTextsItemTextVisible = visible;
  }

  updateFloorplanFreeTextsItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanFreeTexts.forEach((freeText) => (freeText.itemTextVisible = visible));
    this.floorplan.floorplanState.floorplanFreeTextsItemTextVisible = visible;
  }

  updateMeasurementLinesItemTextVisibility(visible: boolean) {
    this.floorplan.measurementLines.forEach((measurementLine) => (measurementLine.itemTextVisible = visible));
    this.floorplan.floorplanState.measurementLinesItemTextVisible = visible;
  }

  updateReferenceMeasureItemTextVisibility(visible: boolean) {
    if (this.floorplan.referenceMeasure) this.floorplan.referenceMeasure.itemTextVisible = visible;
    this.floorplan.floorplanState.referenceMeasureItemTextVisible = visible;
  }

  updatePipelinesItemTextVisibility(visible: boolean) {
    this.floorplan.pipelines.forEach((pipeline) => (pipeline.itemTextVisible = visible));
    this.floorplan.floorplanState.pipelinesItemTextVisible = visible;
  }

  updateCableRoutesItemTextVisibility(visible: boolean) {
    this.floorplan.cableRoutes.forEach((cableRoute) => (cableRoute.itemTextVisible = visible));
    this.floorplan.floorplanState.cableRoutesItemTextVisible = visible;
  }

  updateProtectiveTubesItemTextVisibility(visible: boolean) {
    this.floorplan.protectiveTubes.forEach((protectiveTube) => (protectiveTube.itemTextVisible = visible));
    this.floorplan.floorplanState.protectiveTubesItemTextVisible = visible;
  }

  updateWiresItemTextVisibility(visible: boolean) {
    this.floorplan.wires.forEach((wire) => (wire.itemTextVisible = visible));
    this.floorplan.floorplanState.wiresItemTextVisible = visible;
  }

  updatePatientMonitorsItemTextVisibility(visible: boolean) {
    [...this.floorplan.patientMonitors, ...this.floorplan.patientMonitorPlaceholders].forEach(
      (patientMonitor) => (patientMonitor.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.patientMonitorsItemTextVisible = visible;
  }

  updateCentralStationsItemTextVisibility(visible: boolean) {
    [...this.floorplan.centralStations, ...this.floorplan.centralStationPlaceholders].forEach(
      (centralStation) => (centralStation.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.centralStationsItemTextVisible = visible;
  }

  updateTelemetriesItemTextVisibility(visible: boolean) {
    [...this.floorplan.telemetries, ...this.floorplan.telemetryPlaceholders].forEach(
      (telemetry) => (telemetry.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.telemetriesItemTextVisible = visible;
  }

  updateKvmsItemTextVisibility(visible: boolean) {
    [...this.floorplan.kvms, ...this.floorplan.kvmPlaceholders].forEach((kvm) => (kvm.itemTextVisible = visible));
    this.floorplan.floorplanState.kvmsItemTextVisible = visible;
  }

  updateDisplaysItemTextVisibility(visible: boolean) {
    [...this.floorplan.displays, ...this.floorplan.displayPlaceholders].forEach((display) => (display.itemTextVisible = visible));
    this.floorplan.floorplanState.displaysItemTextVisible = visible;
  }

  updateUtilitiesItemTextVisibility(visible: boolean) {
    [...this.floorplan.utilities, ...this.floorplan.utilityPlaceholders].forEach(
      (utility) => (utility.itemTextVisible = visible),
    );
    this.floorplan.floorplanState.utilitiesItemTextVisible = visible;
  }

  updateIsolationRoomsItemTextVisibility(visible: boolean) {
    this.floorplan.isolationRooms.forEach((isolationRoom) => (isolationRoom.itemTextVisible = visible));
    this.floorplan.floorplanState.isolationRoomsItemTextVisible = visible;
  }

  updateItRoomsItemTextVisibility(visible: boolean) {
    this.floorplan.itRooms.forEach((itRoom) => (itRoom.itemTextVisible = visible));
    this.floorplan.floorplanState.itRoomsItemTextVisible = visible;
  }

  updateOperatingTheatresItemTextVisibility(visible: boolean) {
    this.floorplan.operatingTheatres.forEach((operatingTheatre) => (operatingTheatre.itemTextVisible = visible));
    this.floorplan.floorplanState.operatingTheatresItemTextVisible = visible;
  }

  updateProcedureRoomsItemTextVisibility(visible: boolean) {
    this.floorplan.procedureRooms.forEach((procedureRoom) => (procedureRoom.itemTextVisible = visible));
    this.floorplan.floorplanState.procedureRoomsItemTextVisible = visible;
  }

  updateStandardRoomsItemTextVisibility(visible: boolean) {
    this.floorplan.standardRooms.forEach((standardRoom) => (standardRoom.itemTextVisible = visible));
    this.floorplan.floorplanState.standardRoomsItemTextVisible = visible;
  }
}

export enum FloorplanDisplayMode {
  SCHEMATIC = "SCHEMATIC",
  PRODUCT_IMAGE = "PRODUCT_IMAGE",
}

@Pipe({
  standalone: true,
  name: "localizeFloorplanDisplayMode",
})
export class LocalizeFloorplanDisplayModePipe implements PipeTransform {
  private readonly floorplanDisplayModeLocalizedStringMap = {
    [FloorplanDisplayMode.SCHEMATIC]: $localize`:@@displayMode.schematic:Schematisch`,
    [FloorplanDisplayMode.PRODUCT_IMAGE]: $localize`:@@displayMode.productImage:Produktbilder`,
  };
  transform(value: FloorplanDisplayMode): string {
    return this.floorplanDisplayModeLocalizedStringMap[value];
  }
}

export enum FloorplanSelectionMode {
  SINGLE = "SINGLE",
  MULTI = "MULTI",
}

@Pipe({
  standalone: true,
  name: "localizeFloorplanSelectionMode",
})
export class LocalizeFloorplanSelectionModePipe implements PipeTransform {
  private readonly map = {
    [FloorplanSelectionMode.SINGLE]: $localize`:@@selectionMode.single:Single-Select`,
    [FloorplanSelectionMode.MULTI]: $localize`:@@selectionMode.multi:Multi-Select`,
  };
  transform(value: FloorplanSelectionMode): string {
    return this.map[value];
  }
}
